'use client'

// SUMMARY: The step-by-step page guide - blurs the page except the element the current step explains.

import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslations } from 'next-intl'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { GuideStep } from '@/lib/guides/types'

// Breathing room around the spotlighted element, and how much space the card needs to sit beside it.
const SPOT_PADDING = 8
const CARD_GAP = 12
const CARD_WIDTH = 320
const CARD_SPACE = 210
const VIEWPORT_MARGIN = 16

interface Spot {
	top: number
	left: number
	width: number
	height: number
}

export function GuideOverlay({ steps, onClose }: { steps: GuideStep[]; onClose: () => void }) {
	const t = useTranslations('guide')
	const [index, setIndex] = useState(0)
	const [spot, setSpot] = useState<Spot | null>(null)
	const step = steps[index]
	const isLast = index === steps.length - 1

	const next = useCallback(() => (isLast ? onClose() : setIndex((i) => i + 1)), [isLast, onClose])
	const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [])

	// Tracks the target through resizes, scrolling and late layout (fonts, streamed content). Measuring is
	// deferred into a frame on purpose - a synchronous setState inside an effect cascades renders.
	useEffect(() => {
		let frame = 0
		const measure = () => {
			const el = document.querySelector(step.target)
			if (!el) return setSpot(null)
			const r = el.getBoundingClientRect()
			setSpot({
				top: r.top - SPOT_PADDING,
				left: r.left - SPOT_PADDING,
				width: r.width + SPOT_PADDING * 2,
				height: r.height + SPOT_PADDING * 2,
			})
		}
		const schedule = () => {
			cancelAnimationFrame(frame)
			frame = requestAnimationFrame(measure)
		}
		schedule()
		window.addEventListener('resize', schedule)
		window.addEventListener('scroll', schedule, true)
		return () => {
			cancelAnimationFrame(frame)
			window.removeEventListener('resize', schedule)
			window.removeEventListener('scroll', schedule, true)
		}
	}, [step])

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
			else if (e.key === 'ArrowRight' || e.key === 'Enter') next()
			else if (e.key === 'ArrowLeft') back()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [next, back, onClose])

	if (typeof document === 'undefined') return null

	return createPortal(
		<div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={t('title')}>
			{/* Four panels around the spotlight rather than one masked layer: each really blurs what is behind
			    it, so the explained element stays sharp while the rest of the page recedes. */}
			{spot ? <Panels spot={spot} onClose={onClose} /> : <Panel style={{ inset: 0 }} onClose={onClose} />}

			{spot && (
				<div
					aria-hidden
					className="pointer-events-none absolute rounded-xl ring-2 ring-blue-500 ring-offset-2 ring-offset-background/0 transition-all duration-200"
					style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
				/>
			)}

			<GuideCard
				spot={spot}
				title={t(`steps.${step.id}.title`)}
				body={t(`steps.${step.id}.body`)}
				counter={t('step', { current: index + 1, total: steps.length })}
				isFirst={index === 0}
				isLast={isLast}
				onBack={back}
				onNext={next}
				onClose={onClose}
			/>
		</div>,
		document.body
	)
}

// ===============================
// SECTION: BACKDROP
// ===============================

function Panel({ style, onClose }: { style: React.CSSProperties; onClose: () => void }) {
	return <div className="absolute bg-background/60 backdrop-blur-sm" style={style} onClick={onClose} />
}

function Panels({ spot, onClose }: { spot: Spot; onClose: () => void }) {
	const right = spot.left + spot.width
	const bottom = spot.top + spot.height
	return (
		<>
			<Panel style={{ top: 0, left: 0, right: 0, height: Math.max(0, spot.top) }} onClose={onClose} />
			<Panel style={{ top: bottom, left: 0, right: 0, bottom: 0 }} onClose={onClose} />
			<Panel style={{ top: spot.top, left: 0, width: Math.max(0, spot.left), height: spot.height }} onClose={onClose} />
			<Panel style={{ top: spot.top, left: right, right: 0, height: spot.height }} onClose={onClose} />
		</>
	)
}

// ===============================
// SECTION: STEP CARD
// ===============================

interface CardProps {
	spot: Spot | null
	title: string
	body: string
	counter: string
	isFirst: boolean
	isLast: boolean
	onBack: () => void
	onNext: () => void
	onClose: () => void
}

function GuideCard({ spot, title, body, counter, isFirst, isLast, onBack, onNext, onClose }: CardProps) {
	const t = useTranslations('guide')
	const position = cardPosition(spot)

	return (
		<div
			className={cn(
				'absolute w-[min(20rem,calc(100vw-2rem))] rounded-xl border bg-popover p-4 text-popover-foreground shadow-xl',
				'animate-in fade-in zoom-in-95 duration-150'
			)}
			style={position}
		>
			<div className="mb-1 flex items-start justify-between gap-2">
				<p className="text-xs font-medium text-muted-foreground tabular-nums">{counter}</p>
				<button
					onClick={onClose}
					aria-label={t('close')}
					className="-m-1 cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
				>
					<X className="h-3.5 w-3.5" />
				</button>
			</div>
			<h2 className="mb-1 text-sm font-semibold">{title}</h2>
			<p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
			<div className="mt-4 flex items-center justify-between gap-2">
				<Button size="sm" variant="ghost" onClick={onBack} disabled={isFirst} className="gap-1.5">
					<ArrowLeft className="h-3.5 w-3.5" />
					{t('back')}
				</Button>
				<Button size="sm" onClick={onNext} className="gap-1.5">
					{isLast ? t('done') : t('next')}
					{!isLast && <ArrowRight className="h-3.5 w-3.5" />}
				</Button>
			</div>
		</div>
	)
}

/** Places the card under the spotlight, above it when there is no room below, centered when both fail. */
function cardPosition(spot: Spot | null): React.CSSProperties {
	if (typeof window === 'undefined' || !spot) {
		return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }
	}
	const left = Math.min(
		Math.max(VIEWPORT_MARGIN, spot.left + spot.width / 2 - CARD_WIDTH / 2),
		window.innerWidth - CARD_WIDTH - VIEWPORT_MARGIN
	)
	const spotBottom = spot.top + spot.height
	if (window.innerHeight - spotBottom > CARD_SPACE) return { left, top: spotBottom + CARD_GAP }
	if (spot.top > CARD_SPACE) return { left, bottom: window.innerHeight - spot.top + CARD_GAP }
	return { left, bottom: VIEWPORT_MARGIN }
}
